import { logger } from '@adornis/base/logging.js';

const GUEST_MESSAGE_COUNT_PREFIX = 'gradias.guestMessageCount:';
const GUEST_USER_ID_PREFIX = 'guest:';
const GUEST_USER_ID_STORAGE_KEY = 'gradias.guestUserID';
let currentGuestUserID: string | null = null;

function createGuestUserID(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `${GUEST_USER_ID_PREFIX}${crypto.randomUUID()}`;
  }
  const fallback = `${Date.now()}-${Math.floor(Math.random() * 1_000_000_000)}`;
  return `${GUEST_USER_ID_PREFIX}${fallback}`;
}

export function getGuestUserID(): string {
  if (typeof window === 'undefined') {
    return `${GUEST_USER_ID_PREFIX}server`;
  }

  try {
    if (currentGuestUserID && currentGuestUserID.trim().length > 0) {
      return currentGuestUserID;
    }

    // Stored in sessionStorage (not localStorage) so the guest identity dies when the tab closes.
    // The guestUserID is the only key protecting guest data (it is the owner id), so it must not
    // outlive the browser session — otherwise the next person on a shared PC would inherit it.
    // It survives reloads/navigations within the same tab. (The BaseQL sessionID is forwarded
    // independently in getInitPayload, so this does not affect logged-in sessions.)
    const stored = window.sessionStorage.getItem(GUEST_USER_ID_STORAGE_KEY);
    if (stored && stored.startsWith(GUEST_USER_ID_PREFIX)) {
      currentGuestUserID = stored;
      return currentGuestUserID;
    }

    const next = createGuestUserID();
    currentGuestUserID = next;
    window.sessionStorage.setItem(GUEST_USER_ID_STORAGE_KEY, next);
    return next;
  } catch {
    return createGuestUserID();
  }
}

const GUEST_CONVERSATION_ID_STORAGE_KEY = 'guestConversationId';

/**
 * Liest die ID der aktuellen Gast-Konversation. In sessionStorage, damit der Zeiger – wie die
 * guestUserID – beim Schließen des Tabs verschwindet und nicht von der nächsten Person beansprucht
 * werden kann.
 */
export function getGuestConversationId(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.sessionStorage.getItem(GUEST_CONVERSATION_ID_STORAGE_KEY);
  } catch {
    return null;
  }
}

export function setGuestConversationId(conversationId: string): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(GUEST_CONVERSATION_ID_STORAGE_KEY, conversationId);
  } catch {
    // Ignore errors
  }
}

export function clearGuestConversationId(): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.removeItem(GUEST_CONVERSATION_ID_STORAGE_KEY);
  } catch {
    // Ignore errors
  }
}

/**
 * Übernimmt nach einem Login die Gast-Konversation dieses Tabs (samt Prüfung, Abgaben und Dateien) in das
 * Konto. Einzige Aufrufstelle für alle Login-Arten (Passwort, Registrierung, Google): der Nutzerwechsel in
 * app/page.tsx. Die Gast-ID kommt serverseitig aus der Session, nie aus einem Parameter.
 * Gibt die übernommene Konversations-ID zurück, sonst null.
 */
export async function claimGuestConversationAfterLogin(): Promise<string | null> {
  const guestConversationId = getGuestConversationId();
  if (!guestConversationId) return null;

  try {
    const { claimGuestConversationMutation } = await import('../../db/api/claim-guest-conversation.js');
    await claimGuestConversationMutation(guestConversationId, '')();
    return guestConversationId;
  } catch (error) {
    logger.error({ err: error, context: 'claimGuestConversationAfterLogin' }, 'Guest conversation claim failed');
    return null;
  } finally {
    // Auch bei einem Fehler: sonst versucht jeder Reload eines angemeldeten Nutzers die Übernahme erneut.
    clearGuestConversationId();
    clearGuestMessageCount();
  }
}

/**
 * Prüft, ob eine User-ID eine Guest-ID ist
 */
export function isGuestUserID(userID: string | null | undefined): boolean {
  if (!userID) return false;
  return userID.startsWith(GUEST_USER_ID_PREFIX);
}

/**
 * Gibt die Anzahl der Nachrichten für eine Konversation zurück
 */
export function getGuestMessageCount(conversationId: string): number {
  if (typeof window === 'undefined') return 0;

  try {
    const key = `${GUEST_MESSAGE_COUNT_PREFIX}${conversationId}`;
    const count = window.sessionStorage.getItem(key);
    return count ? parseInt(count, 10) : 0;
  } catch {
    return 0;
  }
}

/**
 * Erhöht die Anzahl der Nachrichten für eine Konversation um 1 und gibt den neuen Zähler zurück
 */
export function incrementGuestMessageCount(conversationId: string): number {
  if (typeof window === 'undefined') return 0;

  try {
    const key = `${GUEST_MESSAGE_COUNT_PREFIX}${conversationId}`;
    const current = getGuestMessageCount(conversationId);
    const next = current + 1;
    window.sessionStorage.setItem(key, String(next));
    return next;
  } catch {
    return 0;
  }
}

/**
 * Löscht alle Message-Count-Daten (z.B. bei Login)
 */
export function clearGuestMessageCount(): void {
  if (typeof window === 'undefined') return;

  try {
    const keysToRemove: string[] = [];
    for (let i = 0; i < window.sessionStorage.length; i++) {
      const key = window.sessionStorage.key(i);
      if (key?.startsWith(GUEST_MESSAGE_COUNT_PREFIX)) keysToRemove.push(key);
    }
    keysToRemove.forEach(key => window.sessionStorage.removeItem(key));
  } catch {
    // Ignore errors
  }
}

let guestLeaveAllowed = false;

/** Lets an intended full-page navigation (Google sign-in) pass the guest leave warning below without the dialog. */
export function allowGuestLeave(): void {
  guestLeaveAllowed = true;
}

/**
 * A download started by navigating (Safari, see chat.tsx): the server answers with an attachment, so the page stays
 * — the warning is only paused for the navigation, not switched off.
 */
export function downloadWithoutLeaveWarning(url: string): void {
  guestLeaveAllowed = true;
  window.location.href = url;
  setTimeout(() => {
    guestLeaveAllowed = false;
  }, 2000);
}

/**
 * Asks before a guest closes or reloads a tab with work in it: that work is reachable only from this tab, and the
 * server deletes it 24 h after the last activity (server/guest-retention.ts). Browsers show their own text; a page
 * cannot word it. A guest who has not started anything leaves without a prompt. Returns the unregister function.
 */
export function registerGuestLeaveWarning(): () => void {
  if (typeof window === 'undefined') return () => {};

  const handleBeforeUnload = (event: BeforeUnloadEvent) => {
    if (guestLeaveAllowed || !getGuestConversationId()) return;
    event.preventDefault();
    // Older browsers only show the dialog when returnValue is set.
    event.returnValue = '';
  };

  window.addEventListener('beforeunload', handleBeforeUnload);
  return () => window.removeEventListener('beforeunload', handleBeforeUnload);
}
